import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/store";
import { Stars } from "@/components/Stars";

const STATUS_STYLE = {
  pending: "bg-[#FFF3CD] text-[#856404]", confirmed: "bg-[#D4EDDA] text-[#155724]",
  completed: "bg-[#002FA7]/10 text-[#002FA7]", declined: "bg-red-50 text-red-700",
  cancelled: "bg-gray-100 text-gray-500",
};

function ReviewForm({ bookingId, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const submit = async () => {
    try { await api.post("/reviews", { booking_id: bookingId, rating, comment }); toast.success("Review submitted"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="mt-3 rounded-xl bg-gray-50 p-3" data-testid={`review-form-${bookingId}`}>
      <Stars value={rating} onChange={setRating} size={22} />
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Share your experience (public)"
        data-testid={`review-comment-${bookingId}`}
        className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#002FA7]" />
      <button data-testid={`review-submit-${bookingId}`} onClick={submit} className="mt-2 rounded-full bg-[#002FA7] px-4 py-1.5 text-sm font-bold text-white">Submit review</button>
    </div>
  );
}

export default function Bookings() {
  const { user, t, priceDisplay } = useApp();
  const [bookings, setBookings] = useState([]);
  const load = () => api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.post(`/bookings/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mx-auto max-w-4xl px-5 py-12 sm:px-8">
      <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tight"><Calendar className="text-[#002FA7]" /> {t("my_bookings")}</h1>
      <p className="mt-2 text-sm text-gray-500" data-testid="bookings-disclaimer">Bookings are free reservation requests to connect you with the other party. Payment, deposits and bonds are arranged directly between you, under their own terms.</p>
      {bookings.length === 0 ? (
        <p className="mt-8 text-gray-400" data-testid="bookings-empty">No bookings yet. Find something to rent!</p>
      ) : (
        <div className="mt-8 space-y-4" data-testid="bookings-list">
          {bookings.map((b) => {
            const iReviewed = b.reviewed_by?.includes(user?.user_id);
            return (
              <div key={b.booking_id} className="rounded-2xl border border-gray-200 p-5" data-testid={`booking-${b.booking_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex gap-3">
                    {b.listing_image && <img src={b.listing_image} alt="" className="h-14 w-14 rounded-lg object-cover" />}
                    <div>
                      <Link to={`/listing/${b.listing_id}`} className="font-bold text-gray-900 hover:text-[#002FA7]">{b.listing_title}</Link>
                      <div className="flex items-center gap-1 text-xs text-gray-500"><MapPin size={12} /> {b.start_date} → {b.end_date} · {b.days} day(s)</div>
                      <div className="mt-1 text-xs font-bold uppercase tracking-widest text-gray-400">
                        {b.my_role === "owner" ? `Renter: ${b.renter_name}` : `Owner: ${b.owner_name}`}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_STYLE[b.status]}`} data-testid={`booking-status-${b.booking_id}`}>{b.status}</span>
                    <div className="mt-1 font-display text-lg font-extrabold text-[#002FA7]">{priceDisplay(b.total, b.currency)}</div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {b.my_role === "owner" && b.status === "pending" && (
                    <>
                      <button data-testid={`confirm-${b.booking_id}`} onClick={() => setStatus(b.booking_id, "confirmed")} className="rounded-full bg-[#155724] px-4 py-1.5 text-sm font-bold text-white">{t("confirm")}</button>
                      <button data-testid={`decline-${b.booking_id}`} onClick={() => setStatus(b.booking_id, "declined")} className="rounded-full bg-[#FF3B30] px-4 py-1.5 text-sm font-bold text-white">{t("decline")}</button>
                    </>
                  )}
                  {b.my_role === "renter" && ["pending", "confirmed"].includes(b.status) && (
                    <button data-testid={`cancel-${b.booking_id}`} onClick={() => setStatus(b.booking_id, "cancelled")} className="rounded-full border border-gray-300 px-4 py-1.5 text-sm font-bold text-gray-600">{t("cancel")}</button>
                  )}
                  {b.status === "confirmed" && (
                    <button data-testid={`complete-${b.booking_id}`} onClick={() => setStatus(b.booking_id, "completed")} className="rounded-full bg-[#002FA7] px-4 py-1.5 text-sm font-bold text-white">{t("mark_completed")}</button>
                  )}
                </div>

                {b.status === "completed" && !iReviewed && <ReviewForm bookingId={b.booking_id} onDone={load} />}
                {b.status === "completed" && iReviewed && <div className="mt-2 text-xs font-semibold text-[#155724]">✓ You reviewed this rental</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
